import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Mail, Copy, Check, Inbox, ArrowRight, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import RichTextEditor from "@/components/RichTextEditor";

/**
 * Phase 151 — Email Settings
 *
 * Per-tenant From name / From email / Reply-To, plus the system-generated
 * inbound address that routes candidate replies back into message threads.
 * Phase 152 — personal signature editor (rich text) for the signed-in user.
 */
export default function CompanyEmailSettings() {
    const { user } = useAuth();
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState("");
    const [signature, setSignature] = useState("");
    const [sigSaving, setSigSaving] = useState(false);

    const canEdit = user?.role === "employer" || user?.role === "admin";

    const load = async () => {
        setLoading(true);
        try {
            const [cfgR, sigR] = await Promise.all([
                api.get("/company/email-settings"),
                api.get("/me/signature").catch(() => ({ data: { signature_html: "" } })),
            ]);
            setCfg(cfgR.data);
            setSignature(sigR.data?.signature_html || "");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    const saveSignature = async () => {
        setSigSaving(true);
        try {
            await api.patch("/me/signature", { signature_html: signature });
            toast.success("Signature saved");
        } catch (err) { toast.error(formatApiError(err)); } finally { setSigSaving(false); }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!canEdit) return;
        setSaving(true);
        try {
            const { data } = await api.patch("/company/email-settings", {
                from_name: cfg.from_name || "",
                from_email: cfg.from_email || "",
                reply_to: cfg.reply_to || "",
                zepto_api_key: cfg._zepto_key_draft ?? undefined,
                zepto_sender_email: cfg.zepto_sender_email || "",
                zepto_sender_name: cfg.zepto_sender_name || "",
                zepto_region: cfg.zepto_region || "in",
            });
            setCfg({ ...data, _zepto_key_draft: undefined });
            toast.success("Email settings saved");
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const [testBusy, setTestBusy] = useState(false);
    const sendTestEmail = async () => {
        setTestBusy(true);
        try {
            const { data } = await api.post("/company/email-settings/test", { to: user?.email });
            if (data.ok) toast.success(`Test email sent via ${cfg.primary_provider} to ${data.to}`);
            else toast.error(`Test failed: ${data.error || "unknown error"}`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setTestBusy(false); }
    };

    const copy = async (text, label) => {
        try {
            await navigator.clipboard?.writeText(text);
            setCopied(label);
            setTimeout(() => setCopied(""), 1500);
            toast.success(`${label} copied`);
        } catch {
            toast.error("Couldn't copy — please copy manually");
        }
    };

    if (loading) return <div className="p-8 text-sm text-muted-foreground font-mono">Loading…</div>;
    if (!cfg) return null;

    return (
        <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6" data-testid="email-settings-page">
            <div className="flex items-start sm:items-center gap-3 flex-wrap">
                <div className="w-11 h-11 bg-ikb/10 text-ikb rounded-lg flex items-center justify-center">
                    <Mail className="w-5 h-5" />
                </div>
                <div>
                    <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Email settings</h1>
                    <p className="text-sm text-muted-foreground">
                        Customize how outbound email looks and where replies land.
                    </p>
                </div>
                <Link
                    to="/settings/email-templates"
                    className="ml-auto text-xs font-mono uppercase tracking-widest text-ikb hover:underline inline-flex items-center gap-1"
                >
                    Templates <ArrowRight className="w-3 h-3" />
                </Link>
            </div>

            <div className="bg-white border border-border rounded-xl p-5 sm:p-6 space-y-5">
                <h2 className="font-display font-semibold text-lg">Outbound sender</h2>
                <p className="text-xs text-muted-foreground -mt-3">
                    Used for every outbound email from this workspace — stage updates, interview invites, and anything a recruiter sends from a candidate profile.
                </p>

                <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">From name</Label>
                        <Input
                            value={cfg.from_name}
                            onChange={(e) => setCfg({ ...cfg, from_name: e.target.value })}
                            disabled={!canEdit}
                            placeholder="Acme Talent"
                            className="h-10 mt-2"
                            data-testid="email-cfg-from-name"
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">Shown as the sender's display name — e.g. "Acme Talent &lt;careers@acme.com&gt;"</p>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">From email</Label>
                        <Input
                            type="email"
                            value={cfg.from_email}
                            onChange={(e) => setCfg({ ...cfg, from_email: e.target.value })}
                            disabled={!canEdit}
                            placeholder="careers@acme.com"
                            className="h-10 mt-2"
                            data-testid="email-cfg-from-email"
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">Verify this domain in Resend before using. Falls back to the platform sender.</p>
                    </div>
                </div>

                <div>
                    <Label className="text-xs uppercase tracking-widest font-bold">Reply-To</Label>
                    <Input
                        type="email"
                        value={cfg.reply_to}
                        onChange={(e) => setCfg({ ...cfg, reply_to: e.target.value })}
                        disabled={!canEdit}
                        placeholder={cfg.inbound_address || "replies@acme.com"}
                        className="h-10 mt-2"
                        data-testid="email-cfg-reply-to"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                        Where candidate replies should land. Use the system inbound address below for automatic conversation threading.
                    </p>
                </div>

                {canEdit && (
                    <div className="pt-3 border-t border-border flex items-center justify-end gap-2">
                        <Button onClick={save} disabled={saving} className="h-10 bg-ikb hover:bg-[#001f7a] text-white" data-testid="email-cfg-save">
                            {saving ? "Saving…" : "Save settings"}
                        </Button>
                    </div>
                )}
            </div>

            {/* Phase 156 — ZeptoMail provider configuration (per-tenant). */}
            <div className="bg-white border border-border rounded-xl p-5 sm:p-6 space-y-5" data-testid="email-cfg-zepto-card">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-amber-50 text-amber-700 rounded-lg flex items-center justify-center shrink-0">
                        <Mail className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="font-display font-semibold text-lg">ZeptoMail provider</h2>
                            <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${cfg.zepto_configured ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-300 bg-slate-50 text-slate-600"}`} data-testid="email-cfg-zepto-status">
                                {cfg.zepto_configured ? "Configured" : "Not configured"}
                            </span>
                            <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-ikb bg-blue-50 text-ikb">
                                Active · {cfg.primary_provider === "zeptomail" ? "ZeptoMail" : "Resend (fallback)"}
                            </span>
                        </div>
                        <p className="text-xs text-foreground/70 mt-0.5">
                            Paste your workspace's ZeptoMail Send-Mail API token to switch outbound mail to your own ZeptoMail account. When set, ZeptoMail is used; otherwise we fall back to the platform-wide Resend sender.
                        </p>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                    <div className="sm:col-span-2">
                        <Label className="text-xs uppercase tracking-widest font-bold">Send-Mail API token</Label>
                        <Input
                            type="password"
                            value={cfg._zepto_key_draft ?? ""}
                            onChange={(e) => setCfg({ ...cfg, _zepto_key_draft: e.target.value })}
                            disabled={!canEdit}
                            placeholder={cfg.zepto_api_key_masked || "Paste your ZeptoMail Send-Mail API token"}
                            className="h-10 mt-2 font-mono"
                            data-testid="email-cfg-zepto-key"
                        />
                        <p className="text-[11px] text-foreground/60 mt-1">
                            Get this from <a className="text-ikb underline" href="https://www.zoho.com/zeptomail/" target="_blank" rel="noreferrer">ZeptoMail Console → Setup → Mail Agents → Send Mail API</a>. We store it encrypted on the company record and never expose it in cleartext after saving.
                        </p>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Verified sender email</Label>
                        <Input
                            type="email"
                            value={cfg.zepto_sender_email}
                            onChange={(e) => setCfg({ ...cfg, zepto_sender_email: e.target.value })}
                            disabled={!canEdit}
                            placeholder="noreply@yourcompany.com"
                            className="h-10 mt-2"
                            data-testid="email-cfg-zepto-sender-email"
                        />
                        <p className="text-[11px] text-foreground/60 mt-1">Must be a verified sender in ZeptoMail.</p>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Sender display name</Label>
                        <Input
                            value={cfg.zepto_sender_name}
                            onChange={(e) => setCfg({ ...cfg, zepto_sender_name: e.target.value })}
                            disabled={!canEdit}
                            placeholder="Acme Talent"
                            className="h-10 mt-2"
                            data-testid="email-cfg-zepto-sender-name"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Region</Label>
                        <select
                            value={cfg.zepto_region || "in"}
                            onChange={(e) => setCfg({ ...cfg, zepto_region: e.target.value })}
                            disabled={!canEdit}
                            className="h-10 mt-2 w-full border border-border rounded-md px-3 bg-white font-mono text-sm"
                            data-testid="email-cfg-zepto-region"
                        >
                            <option value="in">India · api.zeptomail.in</option>
                            <option value="us">US · api.zeptomail.com</option>
                            <option value="eu">EU · api.zeptomail.eu</option>
                        </select>
                        <p className="text-[11px] text-foreground/60 mt-1">Pick the datacenter your ZeptoMail account is hosted in.</p>
                    </div>
                </div>

                {canEdit && (
                    <div className="pt-3 border-t border-border flex items-center justify-between gap-2 flex-wrap">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={sendTestEmail}
                            disabled={testBusy || (!cfg.zepto_configured && cfg._zepto_key_draft === undefined)}
                            className="h-10"
                            data-testid="email-cfg-test"
                            title="Send a one-shot test email to the signed-in user's address"
                        >
                            {testBusy ? "Sending…" : `Send test to ${user?.email || "me"}`}
                        </Button>
                        <Button onClick={save} disabled={saving} className="h-10 bg-ikb hover:bg-[#001f7a] text-white" data-testid="email-cfg-zepto-save">
                            {saving ? "Saving…" : "Save ZeptoMail settings"}
                        </Button>
                    </div>
                )}
            </div>

            <div className="bg-white border border-border rounded-xl p-5 sm:p-6 space-y-4">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 bg-emerald-50 text-emerald-700 rounded-lg flex items-center justify-center shrink-0">
                        <Inbox className="w-4.5 h-4.5" />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-display font-semibold text-lg">Inbound replies</h2>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Candidates who reply to any email we send will land here. Replies are matched to the candidate by email and threaded into their message history automatically.
                        </p>
                    </div>
                </div>

                <div className="border border-border rounded-lg p-4 bg-secondary/30">
                    <Label className="text-[11px] uppercase tracking-widest font-bold text-muted-foreground">Your workspace inbound address</Label>
                    <div className="flex items-center gap-2 mt-2">
                        <code className="text-sm font-mono flex-1 truncate" data-testid="email-cfg-inbound-address">{cfg.inbound_address || "—"}</code>
                        {cfg.inbound_address && (
                            <button
                                type="button"
                                onClick={() => copy(cfg.inbound_address, "Address")}
                                className="h-8 w-8 rounded-md border border-border bg-white inline-flex items-center justify-center hover:border-ikb hover:text-ikb"
                                data-testid="email-cfg-copy-inbound"
                            >
                                {copied === "Address" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                        )}
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2">
                        Set this as your Reply-To above, or forward your existing shared inbox (e.g. hire@yourcompany.com) to this address.
                    </p>
                </div>

                {cfg.inbound_webhook_url && (
                    <details className="text-[11px] text-muted-foreground">
                        <summary className="cursor-pointer font-medium text-foreground">Advanced · configure the Resend inbound webhook</summary>
                        <div className="mt-3 p-3 border border-border rounded-lg bg-neutral-50 space-y-2">
                            <div>Webhook URL (paste into Resend → Inbound Routes):</div>
                            <div className="flex items-center gap-2">
                                <code className="text-[11px] font-mono flex-1 break-all">{cfg.inbound_webhook_url}</code>
                                <button
                                    type="button"
                                    onClick={() => copy(cfg.inbound_webhook_url, "Webhook")}
                                    className="h-7 w-7 rounded-md border border-border bg-white inline-flex items-center justify-center hover:border-ikb hover:text-ikb"
                                    data-testid="email-cfg-copy-webhook"
                                >
                                    {copied === "Webhook" ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                                </button>
                            </div>
                            <div>
                                Resend will POST parsed emails here. Replies are threaded by candidate email automatically; unknown senders land in <Link to="/inbox" className="text-ikb hover:underline">Inbox</Link>.
                            </div>
                        </div>
                    </details>
                )}
            </div>

            {/* Phase 152 — personal signature (sibling card, not nested) */}
            <div className="bg-white border border-border rounded-xl p-5 sm:p-6 space-y-4" data-testid="email-signature-card">
                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 bg-ikb/10 text-ikb rounded-lg flex items-center justify-center shrink-0">
                            <Pencil className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <h2 className="font-display font-bold text-base">Your signature</h2>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Appended at the bottom of candidate emails when "Append my signature" is ticked on Compose.
                            </p>
                        </div>
                    </div>
                    <RichTextEditor
                        value={signature}
                        onChange={setSignature}
                        placeholder={"— Your name\nTitle\nCompany"}
                        minHeight={160}
                        testId="email-signature-editor"
                    />
                    <div className="flex items-center justify-between">
                        <div className="text-[11px] text-muted-foreground">
                            Tip: Include your title, phone, and a scheduling link so candidates can book you back in one click.
                        </div>
                        <Button
                            onClick={saveSignature}
                            disabled={sigSaving}
                            className="h-9 bg-ikb hover:bg-[#001f7a] text-white"
                            data-testid="email-signature-save"
                        >
                            {sigSaving ? "Saving…" : "Save signature"}
                        </Button>
                    </div>
                </div>
        </div>
    );
}
